/**
 * NOLAS 2026, application multi evenement e.labriquerie.net.
 * src/i18n/fr.bilan.ts
 *
 * Rôle :
 *   Sous-catalogue i18n du chantier « bilan d’impact ». Fusionné dans
 *   `messagesFr` par `i18n/fr.ts`, donc utilisable comme n’importe quelle clé.
 *
 * Pourquoi un fichier SÉPARÉ plutôt que d’allonger `fr.ts` :
 *   `fr.ts` est LA dépendance partagée du dépôt. Plusieurs chantiers menés en
 *   parallèle qui y écrivent en même temps s’écrasent. Un catalogue par
 *   chantier supprime la collision par construction. Seules DEUX lignes sont
 *   ajoutées à `fr.ts` : l’import et le déversement.
 *
 * Règles appliquées ici :
 *   • aucune identité d’événement en dur : ni nom, ni date, ni lieu, ni
 *     organisateur. Les jetons `{eventName}`, `{eventTypeDef}` sont substitués
 *     à partir de la base ;
 *   • aucun mot du vocabulaire configurable en dur : les jetons `{lex…}` s’en
 *     chargent. Les libellés des TYPES DE PUBLIC ne sont volontairement pas
 *     ici : ils viennent du serveur, pour que l’écran et le document exporté
 *     ne puissent jamais diverger ;
 *   • apostrophe typographique, et jamais d’apostrophe droite devant un jeton
 *     (en ICU, elle ouvre une séquence d’échappement et fait disparaître le
 *     jeton du rendu).
 *
 * Mikael - chef de projet - 2026-09-10
 */

export const messagesBilanFr: Record<string, string> = {
  // ── Entrée de menu ─────────────────────────────────────────────────────
  'adminNav.bilan': 'Bilan d’impact',

  // Titre du DOCUMENT (onglet, favori, lecteur d’écran), consomme par
  // utils/titresDeRoute.ts. Volontairement pose ici et non dans fr.acces.ts :
  // ce dernier appartient a un autre chantier, et deux chantiers qui ecrivent
  // dans le meme fichier finissent par s’ecraser.
  'bilan.titreDocument': 'Bilan d’impact',

  // ── En-tête d’écran ────────────────────────────────────────────────────
  'bilan.title': 'Bilan d’impact',
  'bilan.lead':
    'Tout ce que l’application sait compter, rassemblé sur une page et exportable en un fichier. Rien n’est recalculé ici : chaque chiffre vient de la table qui détient la donnée, et porte la mention de ce qu’il vaut vraiment.',
  // L’identité affichée en tête d’écran n’est PAS une chaîne du catalogue :
  // elle est composée à partir des champs servis par le serveur, qui les lit
  // en base. Une clé i18n ici obligerait à décider quoi faire des segments
  // vides, décision déjà prise côté serveur par `event_identity_line()`.
  'bilan.generatedAt': 'Document produit le {date}',
  'bilan.organizer': 'Organisateur : {name}',

  // ── Étape de l’événement ───────────────────────────────────────────────
  'bilan.phase.before': 'Avant {eventTypeDef}',
  'bilan.phase.during': 'Pendant {eventTypeDef}',
  'bilan.phase.after': 'Après {eventTypeDef}',
  'bilan.phase.beforeHint':
    'Les compteurs du jour J sont à zéro parce que rien n’a encore eu lieu. C’est l’état attendu, pas une anomalie.',

  // ── États ──────────────────────────────────────────────────────────────
  'bilan.loading': 'Rassemblement des compteurs…',
  'bilan.error.title': 'Le bilan n’a pas pu être chargé.',
  'bilan.error.hint':
    'Le serveur n’a pas répondu, ou sa réponse ne respecte pas le format attendu. Réessayez ; si le problème persiste, vérifiez que la dernière version de l’administration est bien déployée.',
  'bilan.error.retry': 'Réessayer',

  // ── Export ─────────────────────────────────────────────────────────────
  'bilan.export.button': 'Télécharger le document',
  'bilan.export.hint':
    'Fichier tableur, ouvrable tel quel. Il porte l’identité de l’événement, la légende des natures, la liste de ce qui n’est pas mesuré et celle de ce qui a été volontairement écarté.',

  // ── Légende des natures ────────────────────────────────────────────────
  'bilan.legend.title': 'Comment lire ces chiffres',
  'bilan.nature.constate': 'constaté',
  'bilan.nature.constateHint':
    'Chiffre lu en base. Un enregistrement égale un fait : un badge lu, une inscription, un dépôt.',
  'bilan.nature.estime': 'estimé',
  'bilan.nature.estimeHint':
    'Valeur déduite d’un calcul, jamais relevée directement. À lire comme un ordre de grandeur.',
  'bilan.nature.nonMesure': 'non mesuré',
  'bilan.nature.nonMesureHint':
    'La donnée n’est pas collectée. Ce n’est pas un zéro : afficher zéro laisserait croire à un échec là où rien n’a été compté.',

  // ── Répartition des publics ────────────────────────────────────────────
  'bilan.publics.title': 'Inscriptions par type de public',
  'bilan.publics.lead':
    'Comptes actifs. Les comptes d’administration, de démonstration et archivés sont dénombrés à part, plus bas.',
  'bilan.publics.colType': 'Type de public',
  'bilan.publics.colCount': 'Inscrits',
  'bilan.publics.colMinors': 'Dont mineurs',
  'bilan.publics.total': 'Total',
  'bilan.publics.empty':
    'Aucune inscription enregistrée pour l’instant. Le tableau se remplira au fil des inscriptions.',
  'bilan.publics.minorsNote':
    'Le nombre de personnes mineures est une information de pilotage : encadrement, sécurité, obligations légales. Il reste affiché quel que soit le réglage de traitement des comptes mineurs, qui gouverne la manière dont l’application les traite et jamais la visibilité de leur statut.',
  'bilan.publics.unreadable':
    'La table des comptes n’a pas pu être lue : cette répartition est indisponible sur cette instance.',

  // ── Sections d’indicateurs ─────────────────────────────────────────────
  'bilan.section.participation': 'Participation',
  'bilan.section.structures': 'Structures présentes',
  'bilan.section.offres': 'Offres publiées',
  'bilan.section.jour': 'Le jour {eventTypeOf}',
  'bilan.section.activites': '{lexWorkshopPluralCap}',
  'bilan.section.candidatures': 'Candidatures',
  'bilan.section.retours': 'Retours des participants',
  'bilan.section.audience': 'Audience de l’application',

  // ── Répartitions détaillées ────────────────────────────────────────────
  'bilan.contracts.title': 'Offres publiées, par type de contrat',
  'bilan.contracts.colLabel': 'Type de contrat',
  'bilan.contracts.colOffers': 'Offres',
  'bilan.contracts.colPositions': 'Postes annoncés',
  'bilan.contracts.unknown': 'Non précisé',
  'bilan.sectors.title': 'Structures, par département déclaré',
  'bilan.sectors.colCode': 'Département',
  'bilan.sectors.colCount': 'Structures',
  'bilan.sectors.unknown': 'Non renseigné',
  'bilan.sectors.hint':
    'Déduit des deux premiers caractères du code postal saisi par la structure. Une saisie absente ou fantaisiste reste comptée à part, jamais rangée dans un département.',

  // ── Ce qui n’est pas mesuré ────────────────────────────────────────────
  'bilan.notMeasured.title': 'Ce que ce bilan ne mesure pas',
  'bilan.notMeasured.lead':
    'Cette liste vaut autant que la précédente. Un chiffre absent et assumé se défend devant un financeur ; un chiffre faux, jamais.',
  'bilan.notMeasured.colIndicator': 'Indicateur',
  'bilan.notMeasured.colReason': 'Pourquoi il n’est pas disponible',
  'bilan.notMeasured.empty':
    'Tous les indicateurs prévus sont disponibles sur cette instance.',

  // ── Ce qui a été écarté ────────────────────────────────────────────────
  'bilan.exclusions.title': 'Ce qui a été volontairement écarté des chiffres',
  'bilan.exclusions.lead':
    'Ces enregistrements existent en base et n’entrent dans aucun compteur ci-dessus. Ils sont montrés pour que l’écart avec les autres écrans s’explique tout seul.',
  'bilan.exclusions.colLabel': 'Élément',
  'bilan.exclusions.colCount': 'Nombre',
  'bilan.exclusions.colReason': 'Raison du retrait',

  // ── Horizon de conservation ────────────────────────────────────────────
  'bilan.retention.title': 'Jusqu’à quand ce bilan restera complet',
  'bilan.retention.lead':
    'Les passages sur les stands sont conservés un temps limité, puis effacés. Produisez le document avant ces échéances : après, il ne pourra plus être reconstitué.',
  'bilan.retention.anonymisation':
    'Le {date} : le lien entre un passage et une personne est effacé. Le nombre de personnes distinctes cesse alors d’être reconstituable.',
  'bilan.retention.suppression':
    'Le {date} : les passages eux-mêmes sont supprimés.',

  // ── Renvoi vers l’existant ─────────────────────────────────────────────
  'bilan.crossref.title': 'Pour aller plus loin',
  'bilan.crossref.formStats':
    'La répartition détaillée des réponses au formulaire d’inscription (niveau d’études, secteur recherché, canal de connaissance) est déjà servie par l’écran des statistiques du formulaire. Elle n’est pas recopiée ici.',
};
